// 会話履歴の localStorage 永続化。
// インデックス（一覧用メタ情報）と会話本体を別キーで持ち、保存コストを抑える。
import type { ChatState } from '../core/agent.js'

export interface ConversationMeta {
  id: string
  title: string
  updatedAt: number
}

export interface Conversation extends ChatState {
  id: string
  title: string
  createdAt: number
  updatedAt: number
}

const INDEX_KEY = 'ttts-conversations'
const CONV_PREFIX = 'ttts-conv-'

export function generateConversationId(): string {
  const random = Math.random().toString(36).slice(2, 8)
  return `${Date.now().toString(36)}${random}`
}

function readIndex(): ConversationMeta[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (m): m is ConversationMeta =>
        typeof m === 'object' && m !== null && typeof (m as ConversationMeta).id === 'string',
    )
  } catch {
    return []
  }
}

function writeIndex(index: ConversationMeta[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index))
}

/** 更新日時の降順で会話一覧を返す */
export function listConversations(): ConversationMeta[] {
  return readIndex().sort((a, b) => b.updatedAt - a.updatedAt)
}

export function loadConversation(id: string): Conversation | null {
  try {
    const raw = localStorage.getItem(CONV_PREFIX + id)
    if (!raw) return null
    const conv = JSON.parse(raw) as Conversation
    if (typeof conv?.id !== 'string' || !Array.isArray(conv.contents)) return null
    return conv
  } catch {
    return null
  }
}

/**
 * 会話を保存する。localStorage の容量超過時は false を返す
 * （呼び出し側で古い会話の削除を促す）。
 */
export function saveConversation(conv: Conversation): boolean {
  try {
    localStorage.setItem(CONV_PREFIX + conv.id, JSON.stringify(conv))
  } catch {
    return false
  }
  // 本体の保存は成功しているため、インデックス更新の失敗（容量超過等）は
  // 例外を外に漏らさない。次に保存が成功したときに追いつく
  try {
    const index = readIndex().filter((m) => m.id !== conv.id)
    index.push({ id: conv.id, title: conv.title, updatedAt: conv.updatedAt })
    writeIndex(index)
  } catch {
    // noop
  }
  return true
}

export function deleteConversation(id: string): void {
  localStorage.removeItem(CONV_PREFIX + id)
  writeIndex(readIndex().filter((m) => m.id !== id))
}
